import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { removeListQueries, invalidateDetailQueries } from '@/lib/queryHelpers'
import type { Parcel, ApiResponse } from '@/types'

// Polling constants for extraction status
const EXTRACTION_POLL_INTERVAL_MS = 3_000 // 3 seconds
const EXTRACTION_POLL_TIMEOUT_MS = 60_000  // 60 seconds cap

/**
 * Returns the refetchInterval value for a parcel detail query.
 *
 * Polls every 3 s while extraction_status === 'processing', capped at 60 s.
 * The cap is derived from `updated_at` (the moment processing began) so it is
 * stable across remounts — a ref-counter would reset and re-poll indefinitely
 * if the ml service were down.
 *
 * Exported for unit-testing.
 */
export function parcelDetailRefetchInterval(query: { state: { data: Parcel | undefined } }): number | false {
  const parcel = query.state.data
  if (!parcel) return false
  if (parcel.extraction_status !== 'processing') return false

  // Cutoff = updated_at + 60 s (stable across remounts)
  const updatedAt = new Date(parcel.updated_at).getTime()
  if (Date.now() - updatedAt >= EXTRACTION_POLL_TIMEOUT_MS) return false

  return EXTRACTION_POLL_INTERVAL_MS
}

/**
 * Returns the refetchInterval for the parcel LIST query.
 *
 * Polls every 3 s while ANY visible row is still `processing`, so a freshly
 * captured parcel flips to its resolved status (matched / needs_review / no_match)
 * without a manual refresh. Each row's cutoff is `updated_at + 60 s` so the poll
 * stops even if the ml service never responds. Exported for unit-testing.
 */
export function parcelListRefetchInterval(
  query: { state: { data: ApiResponse<Parcel[]> | undefined } },
): number | false {
  const rows = query.state.data?.data
  if (!rows) return false
  const anyProcessing = rows.some(
    (p) =>
      p.extraction_status === 'processing' &&
      Date.now() - new Date(p.updated_at).getTime() < EXTRACTION_POLL_TIMEOUT_MS,
  )
  return anyProcessing ? EXTRACTION_POLL_INTERVAL_MS : false
}

export const parcelKeys = {
  all: ['parcels'] as const,
  lists: () => [...parcelKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...parcelKeys.lists(), filters] as const,
  details: () => [...parcelKeys.all, 'detail'] as const,
  detail: (id: number) => [...parcelKeys.details(), id] as const,
}

// EP-97: server-side sort (allow-listed keys: updated_at/received_at/collected_at)
export function useParcels(page = 1, status?: string, propertyId?: number, from?: string, to?: string, q?: string, sort?: string, sort_order?: string) {
  return useQuery({
    queryKey: parcelKeys.list({ page, status, propertyId, from, to, q, sort, sort_order }),
    queryFn: async () => {
      const params: Record<string, unknown> = { page }
      if (status) params.status = status
      if (propertyId) params.property_id = propertyId
      if (from) params.from = from
      if (to) params.to = to
      if (q) params.q = q
      if (sort) params.sort = sort
      if (sort_order) params.sort_order = sort_order
      const { data } = await api.get<ApiResponse<Parcel[]>>('/api/parcels', { params })
      return data
    },
    refetchInterval: parcelListRefetchInterval,
  })
}

export function useParcel(id: number) {
  return useQuery({
    queryKey: parcelKeys.detail(id),
    queryFn: async () => {
      const { data } = await api.get<ApiResponse<Parcel>>(`/api/parcels/${id}`)
      return data.data
    },
    enabled: !!id,
    refetchInterval: parcelDetailRefetchInterval,
  })
}

interface CreateParcelInput {
  property_id: number
  customer_id?: number | null
  unit_id?: number | null
  recipient_name?: string
  tracking_number?: string
  carrier?: string
  description?: string
  received_at: string
  image_urls?: string[]
}

export function useCreateParcel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateParcelInput) => {
      const { property_id, ...rest } = input
      const { data } = await api.post<ApiResponse<Parcel>>(
        `/api/properties/${property_id}/parcels`,
        { parcel: rest }
      )
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, parcelKeys.lists())
    },
  })
}

interface UpdateParcelInput {
  id: number
  customer_id?: number | null
  unit_id?: number | null
  recipient_name?: string
  tracking_number?: string
  carrier?: string
  description?: string
  image_urls?: string[]
}

export function useUpdateParcel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateParcelInput) => {
      const { data } = await api.patch<ApiResponse<Parcel>>(
        `/api/parcels/${id}`,
        { parcel: input }
      )
      return data.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      // invalidate (not remove) so the currently-mounted list view refetches
      // in place — e.g. resolving a unit on the "Needs review" tab drops the row
      // and decrements the badge immediately, without a manual reload.
      queryClient.invalidateQueries({ queryKey: parcelKeys.lists() })
      invalidateDetailQueries(queryClient, parcelKeys.detail(data.id))
    },
  })
}

export function useDeleteParcel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/parcels/${id}`)
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, parcelKeys.lists())
    },
  })
}

export function useCollectParcel() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<ApiResponse<Parcel>>(`/api/parcels/${id}/collect`)
      return data.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      removeListQueries(queryClient, parcelKeys.lists())
      invalidateDetailQueries(queryClient, parcelKeys.detail(data.id))
    },
  })
}
